import React, { useEffect, useState } from "react";
import "./EventDetails.css";
import { useListManager } from "../../../contexts/list.manager";
import TimeConverter from "../../../functions/TimeConverter";
import ReceptionPhoto from "../../../components/ReceptionPhoto/ReceptionPhoto";
import TitleContainer from "../../../components/TitleContainer/TitleContainer";
import CondoUserService from "../../../services/CondoUserService";
import { createDateFromFormat, formatDate } from "../../../utils/date";
import { CredentialBadge } from "../../../components/CredentialBadge/CredentialBadge";
import { AccessUnit, AccessUnitOptions } from "../../../components/AccessUnitSelector/AccessUnitSelector";
import { capitalize, slugify, toCamelCase } from "../../../utils/string";
import { TiArrowRightThick } from "react-icons/ti";
import { getEventData, getEventTypeName } from "../../../constants/events";
import { AllowAccessBadge } from "../../../components/AllowAccessBadge/AllowAccessBadge";
import { TimeAgo } from "../../../components/TimeAgo/TimeAgo";
import { UnitResponsibilityText, UnitResponsibleRelation, UnitResponsibleRelationText } from "../../../constants/unit";
import { findCredential } from "../../../constants/credentials";
import { Clock } from "lucide-react";
import { TbDoorEnter, TbDoorExit } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { ImportationField } from "../../../utils/dictionary";
import ToolTip from "../../../components/Tooltip/Tooltip";
import { LuChevronDown, LuChevronRight } from "react-icons/lu";
import { UserAgentBrowserIcon, UserAgentOSIcon } from "../../../constants/user-agent";
import { LogOperationColor, LogOperationIcon } from "../../../constants/logs";
import { cn } from "../../../lib/utils";

export function isPanic(event) {
	if (!event) return false;
	return event.detail?.includes('Lado') && (event.name?.toLowerCase().includes('panico') || event.name?.toLowerCase().includes('pânico'));
}

const LogDataNode = ({
	field,
	value,
	entityType,
	level = 0,
}) => {
	const [expanded, setExpanded] = useState(false);

	const isObject =
		value !== null &&
		typeof value === "object";

	const globalDictKey = `${entityType}__${field}`;
	const label = ImportationField[globalDictKey] ?? field;

	// Valor simples
	if (!isObject) {
		return (
			<div
				className="grid grid-cols-[minmax(150px,auto)_minmax(0,1fr)] w-full min-w-0"
			>
				<span
					className="min-w-0 truncate"
				// style={{ paddingLeft: `${level * 20}px` }}
				>
					{label}
				</span>

				<span
					className="min-w-0 truncate"
					title={value != null ? String(value) : ""}
				>
					{value != null ? (
						String(value)
					) : (
						<span className="opacity-40">
							Não consta
						</span>
					)}
				</span>
			</div>
		);
	}

	const entries = Object.entries(value);

	return (
		<div className="grid grid-cols-[minmax(150px,auto)] w-full min-w-0 cursor-pointer">
			{/* Cabeçalho do objeto */}
			<div
				className="min-w-[150px] truncate"
				onClick={() => setExpanded((prev) => !prev)}
			>
				<div
					className="flex items-center gap-1 min-w-0"
				// style={{ paddingLeft: `${level * 20}px` }}
				>
					<button
						type="button"
						className="shrink-0 p-0! bg-transparent!"
						onClick={(e) => {
							e.stopPropagation();
							setExpanded((prev) => !prev);
						}}
					>
						{expanded ? (
							<LuChevronDown size={16} />
						) : (
							<LuChevronRight size={16} />
						)}
					</button>

					<span className="truncate">
						{label}
					</span>
				</div>


			</div>

			{/* Filhos */}
			<div className={`import-item-data w-full min-w-0 ${!expanded ? 'border-transparent!' : ''}`}>
				{expanded ? (
					<>
						{
							entries.map(([childField, childValue], index) => (
								<LogDataNode
									key={`${childField}-${index}`}
									field={childField}
									value={childValue}
									entityType={entityType}
									level={level + 1}
								/>
							))
						}
					</>
				) : (
					<span className="min-w-0 opacity-50" onClick={(e) => {
						e.stopPropagation();
						setExpanded((prev) => !prev);
					}}>
						{Array.isArray(value)
							? `[${entries.length}]`
							: `{${entries.length}}`}
					</span>
				)}
			</div>
		</div>
	);
};

export function formatDestinyName(text, unitName, type, direction) {
	if (text === AccessUnit.Dynamic) {
		return unitName ?? 'common.unknown';
	}
	if (text === AccessUnit.Outdoor) {
		return AccessUnitOptions[text];
	}
	if (text === AccessUnit.Indoor) {
		return AccessUnitOptions[text];
	}
	if (text) {
		return text;
	}
	if (type === 'origin' && direction === 'indoor') {
		return AccessUnitOptions[AccessUnit.Indoor];
	}
	if (type === 'destiny' && direction === 'indoor') {
		return AccessUnitOptions[AccessUnit.Outdoor];
	}
	if (type === 'origin' && direction === 'outdoor') {
		return AccessUnitOptions[AccessUnit.Outdoor];
	}
	if (type === 'destiny' && direction === 'outdoor') {
		return AccessUnitOptions[AccessUnit.Indoor];
	}
	return text;
}

const EventDetails = (props) => {
	const { t } = useTranslation();
	const { closeForm } = useListManager();

	let event = props.event ?? props.lastAccess;

	if (event && Object.values(event)?.length > 0) {
		const formattedEvent = {};
		for (const [key, val] of Object.entries(event)) {
			if (typeof val === 'string') {
				formattedEvent[key] = val?.trim() === "" ? null : val?.trim();
			} else {
				formattedEvent[key] = val;
			}
		}
		event = formattedEvent;
	}

	const close = () => {
		closeForm();
		props.close?.();
	};

	const allow = event?.result === 'Sucesso';

	const eventData = getEventData(event?.name);

	const profileMetadata = [];

	if (event?.profile_tag_schema) {
		for (const field of event.profile_tag_schema) {
			const metadata = event.metadata.find(m => m.name === toCamelCase(field.name));
			if (metadata) {
				profileMetadata.push({
					label: capitalize(field.name),
					value: metadata.value
				})
			} else if (!field.required) {
				profileMetadata.push({
					label: capitalize(field.name),
					value: null
				})
			}
		}
	}

	return (
		<div className={cn(`events-details-wrapper ${isPanic(event) ? 'is-panic-event' : ''}`, props.className)} style={{ ...props.style }}>
			<TitleContainer
				title={`${props.title}${isPanic(event) ? ` (${t('common.panic')})` : ''}`}
				isReception={props.isReception}
				close={() => close()}
			/>
			{props.isLog && (
				<div className="event-details-container h-fit!">
					<div className="grid-event-wrapper p-7! gap-x-4! gap-y-6!">
						<p className="event-details-text">
							<span className="event-details-field-name">Id:</span>{" "}
							{event?.id ?? <span className="opacity-40">Não consta</span>}{" "}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.name")}:</span>{" "}
							{event?.user_name ?? <span className="opacity-40">Não consta</span>}{" "}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">Email:</span>{" "}
							{event?.user_email ?? <span className="opacity-40">Não consta</span>}{" "}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.type")} / {t("common.profile")}:</span>{" "}
							{event?.user_type ?? <span className="opacity-40">Não consta</span>}{" "}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.date")}:</span>{" "}
							{formatDate(new Date(event?.date), false)}{" "}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.time")}:</span>{" "}
							{TimeConverter(event?.date, 3)}{" "}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">Url:</span>{" "}
							<span className="truncate max-w-[20dvw]" title={`${event?.url_requested}`}>
								{event?.url_requested ?? <span className="opacity-40">Não consta</span>}{" "}
							</span>
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.operation")}:</span>{" "}
							<span style={{ '--operationColor': LogOperationColor[event?.operation], '--operationBgColor': 'color-mix(in srgb, var(--operationColor), transparent 95%)' }}>
								{event?.operation ? (
									<span className="inline-flex flex-row gap-2 items-center px-3! py-2! border! rounded-full border-(--operationColor)! bg-(--operationBgColor)! text-(--operationColor)!">
										{React.createElement(LogOperationIcon[event.operation])}
										{event.operation}
									</span>
								) : (
									<span className="opacity-40">Não consta</span>
								)}
							</span>
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">IP:</span>{" "}
							{event?.user_ip ?? <span className="opacity-40">Não consta</span>}{" "}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">Navegador:</span>{" "}
							{event?.user_agent?.browser ? (
								<span className="inline-flex gap-1">
									{event.user_agent.browser}
									{React.createElement(UserAgentBrowserIcon[event.user_agent.browser.toLowerCase()])}
								</span>
							) : (
								<span className="opacity-40">Não consta</span>
							)}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">Sistema operacional:</span>{" "}
							{event?.user_agent?.os?.family ? (
								<span className="inline-flex gap-1">
									{event.user_agent.os.family}
									{React.createElement(UserAgentOSIcon[event.user_agent.os.family.toLowerCase()])}
								</span>
							) : (
								<span className="opacity-40">Não consta</span>
							)}
						</p>
						<div className="event-details-text w-full col-span-4!">
							<span className="event-details-field-name">{t("common.data")}:</span>
							{(event?.log_data && Object.keys(event.log_data).length > 0) ? (
								<div className="import-item-data w-full min-w-0">
									{Object.entries(event?.log_data ?? {}).map(
										([field, value]) => (
											<LogDataNode
												key={field}
												field={field}
												value={value}
												entityType={event.entityType}
											/>
										)
									)}
								</div>
							) : (
								<p className="opacity-40">{t('common.noDataRegistered')}</p>
							)}
						</div>
					</div>
				</div>
			)}
			{props.isEvent && (
				<div className="event-details-container h-fit!">
					<div className="grid-event-wrapper big p-7! gap-x-4! gap-y-6!">
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.license")}:</span>
							{event?.device_serial_number}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.type")}:</span>
							{t(getEventTypeName(event?.type))}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.event")}:</span>
							<span className="flex flex-row items-center gap-[6px]!">
								{eventData?.icon && React.createElement(eventData.icon, { size: 16, className: 'opacity-40!' })}
								{eventData ? t(eventData.name) : event?.name}
							</span>
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.access")}:</span>
							{event?.target_name ?? <span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.details")}:</span>
							{event?.type === 'Acesso' && event?.detail?.startsWith('Lado') ? (
								<span className={`text-(--${event?.detail === "Lado externo" ? 'entryColor' : 'exitColor'}) font-semibold! inline-flex items-center gap-1.5!`}>
									{event?.detail === "Lado externo" ? <TbDoorEnter size={15} /> : <TbDoorExit size={15} />}
									{event?.detail === "Lado externo" ? t("common.entry") : t("common.exit")}
								</span>
							) : (
								<span>{event?.detail ?? <span style={{ opacity: 0.36 }}>{t('common.doesNotContain')}</span>}</span>
							)}
						</p>
						{event?.type === 'Acesso' && findCredential(event) && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.credential")}:</span>
								<CredentialBadge event={event} inline={true} iconSize={15} />
							</p>
						)}
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.data")}:</span>
							{event?.data ?? <span style={{ opacity: 0.36 }}>{t('common.doesNotContain')}</span>}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.user")}:</span>
							{event?.user ?? <span style={{ opacity: 0.36 }}>{t('common.doesNotContain')}</span>}
						</p>
						{event?.profile && event?.profile !== '' && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.profile")}:</span>
								{event?.profile ?? <span style={{ opacity: 0.36 }}>{t('common.doesNotContain')}</span>}
							</p>
						)}
						{event?.additional_access_info && event?.additional_access_info !== '' && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.additionalInfo")}:</span>
								{event?.additional_access_info ?? <span style={{ opacity: 0.36 }}>{t('common.doesNotContain')}</span>}
							</p>
						)}
						{event?.expire_at && event?.expire_at !== '' && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.expiresAt")}:</span>
								{formatDate(new Date(event.expire_at), true)}
							</p>
						)}
						{event?.destiny_data?.unitName && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.unit")}:</span>
								{event?.destiny_data?.unitName}
							</p>
						)}
						{event?.destiny_data?.unitName && event?.destiny_data?.responsibility && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.responsibility")}:</span>
								{t(UnitResponsibilityText[event?.destiny_data?.responsibility])}
							</p>
						)}
						{event?.destiny_data?.unitName && event?.destiny_data?.relation && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.relation")}:</span>
								{event?.destiny_data?.relation === `${UnitResponsibleRelation.OTHER}` ? event?.destiny_data?.otherRelation : t(UnitResponsibleRelationText[event?.destiny_data?.relation])}
							</p>
						)}
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.date")}:</span>
							{formatDate(new Date(event?.fulldate), false)}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.time")}:</span>
							{TimeConverter(event?.fulldate, 3)}{" "}
						</p>
						<p className="event-details-text">
							<span className="event-details-field-name">{t("common.result")}:</span>
							{(() => {
								if (!event.result || event.result.trim() === '') {
									return (
										<span className="opacity-40">{t("common.doesNotContain")}</span>
									);
								}

								const isAccessEvent = ['Acesso', 'Alarme'].includes(event.type);
								const allow = event?.result === 'Sucesso';

								if (isAccessEvent) {
									return (
										<AllowAccessBadge allow={allow} />
									);
								}

								return (
									<span>{t(`${event.result}`)}</span>
								);
							})()}
						</p>
						{event?.car_name && event?.car_name !== '' && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.vehicleName")}:</span>
								{event?.car_name ?? <span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>}
							</p>
						)}
						{event?.car_brand && event?.car_brand !== '' && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.vehicleBrand")}:</span>
								{event?.car_brand ?? <span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>}
							</p>
						)}
						{event?.car_plate && event?.car_plate !== '' && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.vehicleLicensePlate")}:</span>
								{event?.car_plate ?? <span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>}
							</p>
						)}
						{event?.car_color && event?.car_color !== '' && (
							<p className="event-details-text">
								<span className="event-details-field-name">{t("common.vehicleColor")}:</span>
								{event?.car_color ?? <span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>}
							</p>
						)}
						<p className="event-details-text">
							<span className="event-details-field-name">
								{event?.type === 'Acesso' && !allow ? t("common.reason") : t("common.info")}
							</span>
							{event?.info ?? <span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>}
						</p>
						{profileMetadata.map((field, index) => (
							<p key={index} className="event-details-text">
								<span className="event-details-field-name">
									{field.label}
								</span>
								<span>
									{field.value ? field.value : (
										<span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>
									)}
								</span>
							</p>
						))}
					</div>
				</div>
			)}
			{props.isReception && (
				<div className="event-details-container">
					<div className={`event-details-column ${allow ? '' : 'access-denied'}`}>
						<div className="flex flex-col items-center justify-center min-h-full! w-full!">
							<ReceptionPhoto
								urlLogo={event?.userData?.signed_url_photo?.replace("[:STORAGE_ADDRESS:]", window.location.hostname)}
								name={capitalize(event?.user || event?.name)}
								showUserLink={true}
								userUuid={event?.userData?.uuid}
							/>
							{event?.profile && (
								<span className="inline-flex mx-auto! text-center w-fit px-3! py-1! text-(--textColor) muted-sm my-0! mt-0.5!">{event.profile}</span>
							)}
							<p className="text-(--textColor) mt-1.5!">
								<span className="inline-flex text-[11px]! leading-3.75! opacity-60!">
									<Clock size={12} className="inline-flex mr-[0.24rem]! pt-px! opacity-75!" />
									<span>
										<TimeAgo date={event.fulldate} simple={true} showText={true} />
									</span>
								</span>
							</p>
							<CredentialBadge event={event} />
						</div>
					</div>
					<div className="event-details-column flex justify-center items-center flex-col box-border! h-full! flex-1 px-6! py-4!">
						<div className="flex flex-row items-center h-fit! justify-between w-full border-b! border-(--borderColor)! pb-3! mb-4.5!">
							<div className="flex flex-row-reverse items-center gap-3!">
								<h2 className="w-fit font-semibold! text-[15px]! m-0! p-0! leading-1!">
									{event?.target_name}
								</h2>
								<span
									style={{
										'--bgColor': event?.detail === "Lado externo" ? 'var(--entryBackgroundColor)' : 'var(--exitBackgroundColor)',
										'--color': event?.detail === "Lado externo" ? 'var(--entryColor)' : 'var(--exitColor)'
									}}
									className={`inline-flex flex-row items-center gap-1! text-xs! text-(--color)! bg-(--bgColor) rounded-(--borderRadius)! border! border-(--color)! px-3! py-1.5!`}
								>
									{event?.detail === "Lado externo" ? (
										<TbDoorEnter size={15} />
									) : (
										<TbDoorExit size={15} />
									)}
									<b className="text-[12px]! font-semibold!">
										{event?.detail === "Lado externo" ? t("common.entry") : t("common.exit")}
									</b>
								</span>
							</div>
							<AllowAccessBadge allow={allow} />
						</div>
						<div className="grid-event-wrapper">
							{event?.type === 'Acesso' && findCredential(event) && (
								<p className={`event-details-text`}>
									<span className="event-details-field-name">
										{t("common.credential")}
									</span>
									<span>
										<CredentialBadge event={event} inline={true} iconSize={15} />
									</span>
								</p>
							)}
							<p className={`event-details-text`}>
								<span className="event-details-field-name">
									{t("common.origin")}
								</span>
								<span>
									{event?.detail === "Lado externo" ? (
										<span>
											{t(formatDestinyName(event.exit_name, event?.userData?.unit_name, 'origin', 'outdoor'))}
										</span>
									) : (
										<span>
											{t(formatDestinyName(event.entry_name, event?.userData?.unit_name, 'origin', 'indoor'))}
										</span>
									)}
								</span>
							</p>
							<p className={`event-details-text`}>
								<span className="event-details-field-name">
									{t("common.destiny")}
								</span>
								<span>
									{event?.detail === "Lado externo" ? (
										<span>
											{t(formatDestinyName(event.entry_name, event?.userData?.unit_name, 'destiny', 'outdoor'))}
										</span>
									) : (
										<span>
											{t(formatDestinyName(event.exit_name, event?.userData?.unit_name, 'destiny', 'indoor'))}
										</span>
									)}
								</span>
							</p>
							<p className="event-details-text">
								<span className="event-details-field-name">
									{t("common.date")}
								</span>
								<span>
									{formatDate(new Date(event?.fulldate), false)}{" "}
								</span>
							</p>
							<p className="event-details-text">
								<span className="event-details-field-name">
									{t("common.time")}
								</span>
								<span>
									{TimeConverter(event?.fulldate, 3)}{" "}
								</span>
							</p>
							{event?.data && (event?.name_code === 13 || (!allow && event?.type_code === 3)) && (
								<p className="event-details-text">
									<span className="event-details-field-name">
										{event?.name_code === 13 ? t("common.operator") : (!allow && event?.type_code === 3 ? 'Valor Cred' : t("common.data"))}
									</span>
									<span>
										{event.data ? event.data : (
											<span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>
										)}
									</span>
								</p>
							)}
							{event?.additional_access_info && (
								<p className="event-details-text">
									<span className="event-details-field-name">
										{t("common.info")}
									</span>
									<span>
										{event?.additional_access_info}
									</span>
								</p>
							)}
							{event?.expire_at && (
								<p className="event-details-text">
									<span className="event-details-field-name">
										{t("common.expiration")}
									</span>
									<span>
										{formatDate(new Date(event.expire_at), true)}
									</span>
								</p>
							)}
							{!allow && (event?.info || event?.name) && (
								<p className="event-details-text">
									<span className="event-details-field-name">
										{t("common.reason")}
									</span>
									<span>
										{event.info ? event.info : (
											<span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>
										)}
									</span>
								</p>
							)}
							<p className="event-details-text">
								<span className="event-details-field-name">
									{t("common.unit")}
								</span>
								<span>
									{event.userData?.unit_name ? event.userData.unit_name : (
										<span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>
									)}
								</span>
							</p>
							{event?.destiny_data?.responsibility && (
								<p className="event-details-text">
									<span className="event-details-field-name">
										{t("common.responsible")}
									</span>
									<span>
										{event.destiny_data?.responsibility ? t(UnitResponsibilityText[event.destiny_data.responsibility]) : (
											<span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>
										)}
									</span>
								</p>
							)}
							{event?.destiny_data?.relation && (
								<p className="event-details-text">
									<span className="event-details-field-name">
										{t("common.relation")}
									</span>
									<span>
										{event.destiny_data?.relation === `${UnitResponsibleRelation.OTHER}` ? event.destiny_data.otherRelation : t(UnitResponsibleRelationText[event.destiny_data.relation])}
									</span>
								</p>
							)}
							{event?.car_plate && (
								<p className="event-details-text">
									<span className="event-details-field-name">
										{t("common.plate")}
									</span>
									<span>
										{event?.car_plate && event?.car_plate !== "" ? event?.car_plate : (
											<span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>
										)}
									</span>
								</p>
							)}
							{(event?.car_brand || event?.car_name || event?.car_color) && (
								<p className="event-details-text">
									<span className="event-details-field-name">
										{t("common.vehicle")}
									</span>
									<span>
										{(event?.car_brand || event?.car_name) ? (
											<span className='flex flex-row items-center'>
												{event?.car_brand && event?.car_brand !== "" && (
													<span>
														{event?.car_brand}
													</span>
												)}
												{event?.car_name && event?.car_name !== "" && (
													<span className="ml-1!">
														{event?.car_name}
													</span>
												)}
												{event?.car_color && event?.car_color !== "" && (
													<span className="opacity-70 inline-flex items-center">
														<span className="inline-flex mx-1! text-[9px]!">•</span>
														<span>{event?.car_color}</span>
													</span>
												)}
											</span>
										) : (
											<span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>
										)}
									</span>
								</p>
							)}
							{profileMetadata.map((field, index) => (
								<p key={index} className="event-details-text">
									<span className="event-details-field-name">
										{field.label}
									</span>
									<span>
										{field.value ? field.value : (
											<span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>
										)}
									</span>
								</p>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default EventDetails;
